import { Divider } from 'components/ui/Divider';
import { FormSectionSimple } from 'components/layout/Section/FormSectionSimple';
import { NsKey } from 'enums/NsKey';
import { InputFactory } from 'factories/InputFactory';
import { legalEntityLayoutFieldsDisabledRules } from './layout-config/legalEntityLayoutConfig';
import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import { BaseLegalEntityFormFieldNames } from 'shared/enums/baseLegalEntityFormFieldNames';
import { LegalEntityFormFieldNames } from 'features/new-actor/enums/legalEntityFormFieldNames';
import { PersonalInformationFieldNames } from 'features/new-actor/enums/personalInformationFieldNames';
import { legalEntityInputsConfig } from 'features/new-actor/form/inputs-config/optim/legalEntityInputsConfig';
import { LegalEntityFormValues } from 'features/new-actor/types/legalEntityFormValues';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useCallback, useMemo } from 'react';
import { BaseLayoutProps } from 'types/form/baseLayoutProps';
import { InputsConfig } from 'types/form/inputsConfig';
import { UpiContactSection } from 'features/subscription-process/registration-process/components/upi/UpiContactSection';
import { SyndicSiretPrefill } from '../SyndicSiretPrefill';
import { LegalEntityFinancialsSection } from './LegalEntityFinancialsSection';
import { LegalEntityFinancialsLoader } from './LegalEntityFinancialsLoader';

export const LegalEntityLayout: React.FC<BaseLayoutProps> = ({ isEdit, layoutVariant, legalEntityOptions }) => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);

  const disabledFields = useMemo(() => {
    return legalEntityLayoutFieldsDisabledRules[layoutVariant]
  }, [layoutVariant])

  const renderField = useCallback(<T extends keyof LegalEntityFormValues>(
    fieldName: T
  ) => {
    return (
      <InputFactory
        config={legalEntityInputsConfig as InputsConfig<T>}
        fieldName={fieldName}
        disabledFields={isEdit ? disabledFields as T[] : undefined}
      />
    );
  }, [disabledFields, isEdit]);

  return (
    <div className="font-lato flex flex-col gap-4">
      {/* Pré-remplissage du nom depuis le registre des copropriétés quand le SIRET est un syndic connu */}
      <SyndicSiretPrefill />
      <FormSectionSimple title={t('ACTOR_FORM.LABELS.COMPANY_SECTION')}>
        <>
          <div className="col-span-3">
            {renderField(LegalEntityFormFieldNames.CompanyName)}
          </div>
          <div className="col-span-3">
            {renderField(BaseLegalEntityFormFieldNames.LegalReason)}
          </div>
          <div className="col-span-3">
            {renderField(PersonalInformationFieldNames.Phone)}
          </div>
          <div className="col-span-3">
            {renderField(PersonalInformationFieldNames.Email)}
          </div>
          <div className="col-span-3">
            {renderField(BaseLegalEntityFormFieldNames.Capital)}
          </div>
          <div className="col-span-3">
            {renderField(BaseLegalEntityFormFieldNames.RcsCity)}
          </div>
          <div className="col-span-3">
            {renderField(BaseLegalEntityFormFieldNames.RegistrationNumber)}
          </div>
          <div className="col-span-3">
            {renderField(BaseLegalEntityFormFieldNames.RcsNumber)}
          </div>
          <div className="col-span-6">
            <Divider />
          </div>
          <div className="col-span-3">
            {renderField(BaseLegalEntityFormFieldNames.OriasNumber)}
          </div>
          <div className="col-span-3">
            {renderField(BaseLegalEntityFormFieldNames.Rcp)}
          </div>
        </>
      </FormSectionSimple>
      <FormSectionSimple title={t('COMMON.LABELS.COORDINATE')}>
        <>
          <div className="col-span-6">
            {renderField(AddressFormFieldNames.Address)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.AdditionalAddress)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.PostalCode)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.City)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.Country)}
          </div>
        </>
      </FormSectionSimple>
      <UpiContactSection compact={legalEntityOptions?.compact} />
      {legalEntityOptions?.financials && (
        <LegalEntityFinancialsLoader>
          <LegalEntityFinancialsSection />
        </LegalEntityFinancialsLoader>
      )}
    </div>
  );
};
